import { SPORTS } from '../../data/constants';
import { legacySportKey } from '../../data/sports';
import { StatCards, SectionCard, HBar, WeeklyBarChart, EmptyState, last8Weeks } from './shared';
import BjjPromotionSummary from '../BjjPromotionSummary';

const GRAPPLING_SPORTS = ['gi_bjj', 'nogi_bjj', 'wrestling_grappling', 'judo'];

export default function GrapplingAnalysis({ records, promotions = [] }) {
    if (!records.length) return <EmptyState text="그래플링 기록이 없어요." />;

    const totalMin = records.reduce((a, r) => a + (+r.duration || 0), 0);
    const sparringRecs = records.filter(r => r.sparring);
    const totalSparRounds = records.reduce((a, r) => a + (+r.sparringRounds || 0), 0);

    // 종목별 분포
    const sportCounts = {};
    records.forEach(r => {
        const k = legacySportKey(r);
        sportCounts[k] = (sportCounts[k] || 0) + 1;
    });
    const maxSport = Math.max(...Object.values(sportCounts), 1);

    // 기술 분석
    const techMap = {};
    let successTotal = 0, attemptTotal = 0;
    records.forEach(r => {
        (r.techniques || []).filter(t => t.name).forEach(t => {
            if (!techMap[t.name]) techMap[t.name] = { success: 0, attempt: 0, received: 0, total: 0 };
            techMap[t.name].total++;
            if (t.result === 'success') { techMap[t.name].success++; successTotal++; }
            else if (t.result === 'attempt') { techMap[t.name].attempt++; attemptTotal++; }
            else if (t.result === 'received') techMap[t.name].received++;
        });
    });

    const topTechs = Object.entries(techMap)
        .sort((a, b) => b[1].total - a[1].total)
        .slice(0, 7);
    const maxTech = topTechs[0]?.[1].total || 1;

    // 포지션 분석
    const posMap = {};
    records.forEach(r => {
        (r.techniques || []).filter(t => t.position).forEach(t => {
            posMap[t.position] = (posMap[t.position] || 0) + 1;
        });
    });
    const topPositions = Object.entries(posMap).sort((a, b) => b[1] - a[1]).slice(0, 5);
    const maxPos = topPositions[0]?.[1] || 1;

    // 주간 스파링 라운드
    const weeklySpar = last8Weeks(records, recs => recs.reduce((a, r) => a + (+r.sparringRounds || 0), 0));

    const overallSuccessRate = (successTotal + attemptTotal) > 0
        ? Math.round((successTotal / (successTotal + attemptTotal)) * 100)
        : null;

    return (
        <>
            <StatCards items={[
                { val: `${records.length}회`, label: '총 세션' },
                { val: `${Math.round(totalMin / 60)}h`, label: '총 시간' },
                { val: `${sparringRecs.length}회`, label: '스파링 세션' },
                { val: `${totalSparRounds}R`, label: '총 스파링 라운드' },
            ]} />

            <SectionCard title="종목 분포" desc="어떤 그래플링을 많이 했는지">
                {GRAPPLING_SPORTS.filter(k => sportCounts[k]).map(k => (
                    <HBar key={k} label={SPORTS[k]?.name || k} value={sportCounts[k]} max={maxSport} color={SPORTS[k]?.color || '#9b8cff'} suffix="회" />
                ))}
            </SectionCard>

            {promotions.length > 0 && <BjjPromotionSummary promotions={promotions} compact />}

            <SectionCard title="기술 분석" desc="기록한 기술의 성공/시도 비율">
                {overallSuccessRate !== null && (
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, color: 'var(--text2)' }}>
                            <span>전체 기술 성공률</span>
                            <span style={{ fontFamily: 'DM Mono,monospace', color: 'var(--accent)', fontWeight: 600 }}>{overallSuccessRate}%</span>
                        </div>
                        <div style={{ height: 8, background: 'var(--bg3)', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${overallSuccessRate}%`, background: 'var(--accent)', borderRadius: 4 }} />
                        </div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 11, color: 'var(--text3)' }}>
                            <span>성공 {successTotal}회</span>
                            <span>시도 {attemptTotal}회</span>
                        </div>
                    </div>
                )}

                {topTechs.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 0' }}>기록된 기술이 없어요</div>
                ) : (
                    <>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>자주 쓴 기술 TOP {topTechs.length}</div>
                        {topTechs.map(([name, stat]) => {
                            const rate = (stat.success + stat.attempt) > 0
                                ? Math.round(stat.success / (stat.success + stat.attempt) * 100) : null;
                            return (
                                <div key={name} style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                        <span style={{ color: 'var(--text2)' }}>{name}</span>
                                        <span style={{ fontFamily: 'DM Mono,monospace', color: 'var(--text3)', fontSize: 11 }}>
                                            {stat.total}회{rate !== null ? ` · 성공률 ${rate}%` : ''}
                                        </span>
                                    </div>
                                    <div style={{ height: 5, background: 'var(--bg3)', borderRadius: 3 }}>
                                        <div style={{ height: '100%', width: `${(stat.total / maxTech) * 100}%`, background: '#9b8cff', borderRadius: 3 }} />
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </SectionCard>

            {topPositions.length > 0 && (
                <SectionCard title="포지션 분포" desc="자주 기록한 포지션">
                    {topPositions.map(([pos, cnt]) => (
                        <HBar key={pos} label={pos} value={cnt} max={maxPos} color="#9b8cff" suffix="회" />
                    ))}
                </SectionCard>
            )}

            <SectionCard title="스파링 추이" desc="주별 스파링 라운드 수">
                <WeeklyBarChart data={weeklySpar} label="주간 스파링 라운드" color="#9b8cff" />
                <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: 'var(--text3)' }}>
                    <span>스파링 비율 {records.length ? Math.round(sparringRecs.length / records.length * 100) : 0}%</span>
                    <span>세션 평균 {sparringRecs.length ? (totalSparRounds / sparringRecs.length).toFixed(1) : 0}R</span>
                </div>
            </SectionCard>
        </>
    );
}
